import React, { useState, useCallback, memo } from 'react';
import PropTypes from 'prop-types';

import { injectIntl } from 'react-intl';
import desktopNotificationManager from 'services/desktopNotificationManager';
import Block from './Block';

const NotificationBlockDeniedComponent = ({ intl, hasIgnore = true, onIgnore }) => (
    <Block
        bgColor="#ff0540"
        textColor="#FFFFFF"
        title={intl.formatMessage({ id: 'notification.permission.denied.title' })}
        description={intl.formatMessage({ id: 'notification.permission.denied.description' })}
        hasIgnore={hasIgnore}
        onIgnore={onIgnore}
        isVisible
    />
);

NotificationBlockDeniedComponent.propTypes = {
    intl: PropTypes.any.isRequired,
    hasIgnore: PropTypes.bool,
    onIgnore: PropTypes.func,
};

export const NotificationBlockDenied = injectIntl(memo(NotificationBlockDeniedComponent));

const NotificationBlockDefaultComponent = (props) => {
    const { intl, hasIgnore = true, onRequestPermission, onIgnore } = props;

    return (
        <Block
            bgColor="#016FB9"
            textColor="#FFFFFF"
            title={intl.formatMessage({ id: 'notification.permission.prompt.title' })}
            description={intl.formatMessage({ id: 'notification.permission.prompt.description' })}
            hasIgnore={hasIgnore}
            onClick={onRequestPermission}
            hasCTA
            CTALabel={intl.formatMessage({ id: 'global.button.activate' })}
            onIgnore={onIgnore}
            isVisible
            hasRadius={false}
        />
    );
};

NotificationBlockDefaultComponent.propTypes = {
    intl: PropTypes.any.isRequired,
    hasIgnore: PropTypes.bool,
    onIgnore: PropTypes.func,
    onRequestPermission: PropTypes.func.isRequired,
};

const NotificationBlockDefault = injectIntl(memo(NotificationBlockDefaultComponent));

const NotificationBlockGrantedComponent = () => (
    <Block
        bgColor="#53FF40"
        title={(
            <>
                Permissions Accordé
            </>)}
        description="Les notifs desktop sont actives"
        hasIgnore={false}
        isVisible
    />
);

const NotificationBlockGranted = injectIntl(memo(NotificationBlockGrantedComponent)); // eslint-disable-line

const NotificationBlock = ({ hasIgnore = true }) => {
    const [permission, setPermission] = useState(desktopNotificationManager.getPermission());
    const isSupported = desktopNotificationManager.isSupported();
    
    const handleIgnore = useCallback(() => {
        desktopNotificationManager.ignorePromptUnitDelay();
        setPermission('granted');
    }, []);
    
    const handleActivate = useCallback(() => {
        desktopNotificationManager.askNotificationPermission()
            .then(setPermission);
    }, []);

    if (!desktopNotificationManager.shouldAskPermission()) {
        return null;
    }

    if (isSupported && permission) {
        if (permission === 'denied') {
            return (
                <NotificationBlockDenied
                    hasIgnore
                    onIgnore={handleIgnore}
                />
            );
        }

        if (permission === 'granted') {
            return null;
        }

        return (
            <NotificationBlockDefault
                hasIgnore={hasIgnore}
                onRequestPermission={handleActivate}
                onIgnore={handleIgnore}
            />
        );
    }

    return null;
};

NotificationBlock.propTypes = {
    hasIgnore: PropTypes.bool,
};

export const Component = memo(NotificationBlock);

export default Component;
